import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    AcademicCapIcon,
    MapPinIcon,
    CheckBadgeIcon,
    XMarkIcon
} from '@heroicons/react/24/outline'
import { universityAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import UniversityForm from '../../components/Admin/UniversityForm'
import PermissionWrapper from '../../components/Admin/PermissionWrapper'
import toast from 'react-hot-toast'

const AdminUniversities = () => {
    return (
        <PermissionWrapper requiredPermission="universities">
            <AdminUniversitiesContent />
        </PermissionWrapper>
    )
}

const AdminUniversitiesContent = () => {
    const [filters, setFilters] = useState({
        search: '',
        fee_range: '',
        approval: '',
        featured: '',
        status: '',
        page: 1,
        limit: 10,
        sort: 'order'
    })
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingUniversity, setEditingUniversity] = useState(null)
    const queryClient = useQueryClient()

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['admin-universities', filters],
        queryFn: () => universityAPI.getAdminUniversities(filters),
        keepPreviousData: true,
    })

    const createMutation = useMutation({
        mutationFn: universityAPI.createUniversity,
        onSuccess: () => {
            toast.success('University created successfully!')
            queryClient.invalidateQueries(['admin-universities'])
            setIsModalOpen(false)
            setEditingUniversity(null)
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to create university')
        },
    })

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => universityAPI.updateUniversity(id, data),
        onSuccess: () => {
            toast.success('University updated successfully!')
            queryClient.invalidateQueries(['admin-universities'])
            setIsModalOpen(false)
            setEditingUniversity(null)
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update university')
        },
    })

    const deleteMutation = useMutation({
        mutationFn: universityAPI.deleteUniversity,
        onSuccess: () => {
            toast.success('University deleted successfully!')
            queryClient.invalidateQueries(['admin-universities'])
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to delete university')
        },
    })

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }))
    }

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }))
    }

    const handleEdit = (university) => {
        setEditingUniversity(university)
        setIsModalOpen(true)
    }

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this university?')) {
            deleteMutation.mutate(id)
        }
    }

    const handleFormSubmit = (data) => {
        if (editingUniversity) {
            updateMutation.mutate({ id: editingUniversity._id, data })
        } else {
            createMutation.mutate(data)
        }
    }

    const handleCancel = () => {
        setIsModalOpen(false)
        setEditingUniversity(null)
    }


    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message="Failed to load universities" onRetry={refetch} />
            </div>
        )
    }

    const universities = data?.data?.universities || []
    const pagination = data?.data?.pagination || {}

    // Debug logging
    console.log('AdminUniversities Debug:', {
        isLoading,
        error,
        data,
        universities,
        pagination
    })

    return (
        <>
            <Helmet>
                <title>Manage Universities | Margdarshak Admin</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                {/* Quick Actions */}
                <div className="bg-white shadow-sm border-b border-gray-200">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex justify-end">
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="btn-primary"
                            >
                                <PlusIcon className="w-5 h-5 mr-2" />
                                Add University
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white border-b border-gray-200">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search universities..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                                />
                            </div>
                            <select
                                value={filters.fee_range}
                                onChange={(e) => handleFilterChange('fee_range', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                            >
                                <option value="">All Fee Ranges</option>
                                <option value="below_2_lakhs">Below 2 Lakhs</option>
                                <option value="above_2_lakhs">Above 2 Lakhs</option>
                            </select>
                            <select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="draft">Draft</option>
                            </select>
                            <select
                                value={filters.featured}
                                onChange={(e) => handleFilterChange('featured', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                            >
                                <option value="">All Universities</option>
                                <option value="true">Featured</option>
                                <option value="false">Regular</option>
                            </select>
                            <select
                                value={filters.sort}
                                onChange={(e) => handleFilterChange('sort', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                            >
                                <option value="order">Sort by Order</option>
                                <option value="name">Sort by Name</option>
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="featured">Featured First</option>
                                <option value="status">Sort by Status</option>
                            </select>
                            <button
                                onClick={() => setFilters({
                                    search: '',
                                    fee_range: '',
                                    approval: '',
                                    featured: '',
                                    status: '',
                                    page: 1,
                                    limit: 10,
                                    sort: 'order'
                                })}
                                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center"
                            >
                                <XMarkIcon className="w-4 h-4 mr-2" />
                                Clear Filters
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <LoadingSpinner size="lg" />
                        </div>
                    ) : (
                        <>
                            {/* Universities Table */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    University
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Order
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Fee Range
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Approvals
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {universities.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                                        <div className="flex flex-col items-center">
                                                            <AcademicCapIcon className="w-12 h-12 text-gray-300 mb-4" />
                                                            <p className="text-lg font-medium">No universities found</p>
                                                            <p className="text-sm">Get started by adding your first university.</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                universities.map((university) => (
                                                    <tr key={university._id} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-4">
                                                                    {university.logo?.url ? (
                                                                        <img
                                                                            src={university.logo.url}
                                                                            alt={university.name}
                                                                            className="w-12 h-12 rounded-lg object-cover"
                                                                        />
                                                                    ) : (
                                                                        <AcademicCapIcon className="w-6 h-6 text-primary-600" />
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {university.name}
                                                                    </div>
                                                                    <div className="text-sm text-gray-500">
                                                                        {university.featured ? 'Featured University' : 'Regular University'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {university.order || 0}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${university.fee_range?.type === 'below_2_lakhs'
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-blue-100 text-blue-800'
                                                                }`}>
                                                                {university.fee_range?.type === 'below_2_lakhs' ? 'Below 2 Lakhs' : 'Above 2 Lakhs'}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex flex-wrap gap-1">
                                                                {university.approvals?.map((approval) => (
                                                                    <span
                                                                        key={approval._id || approval}
                                                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800"
                                                                    >
                                                                        <CheckBadgeIcon className="w-3 h-3 mr-1" />
                                                                        {approval.name || approval}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex flex-col gap-1">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${university.status === 'active'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : university.status === 'inactive'
                                                                        ? 'bg-red-100 text-red-800'
                                                                        : 'bg-yellow-100 text-yellow-800'
                                                                    }`}>
                                                                    {university.status || 'active'}
                                                                </span>
                                                                {university.featured && (
                                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-800">
                                                                        Featured
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                            <div className="flex justify-end space-x-2">
                                                                <button
                                                                    onClick={() => handleEdit(university)}
                                                                    className="text-accent-600 hover:text-accent-900 p-1"
                                                                >
                                                                    <PencilIcon className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(university._id)}
                                                                    className="text-red-600 hover:text-red-900 p-1"
                                                                >
                                                                    <TrashIcon className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Pagination */}
                            {pagination.total_pages > 1 && (
                                <div className="flex items-center justify-between mt-6">
                                    <div className="text-sm text-gray-700">
                                        Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of {pagination.total_count} results
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => handlePageChange(pagination.current_page - 1)}
                                            disabled={pagination.current_page === 1}
                                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(pagination.current_page + 1)}
                                            disabled={pagination.current_page === pagination.total_pages}
                                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Add/Edit Modal */}
                <AnimatePresence>
                    {isModalOpen && (
                        <UniversityForm
                            onSubmit={handleFormSubmit}
                            initialData={editingUniversity}
                            onCancel={handleCancel}
                        />
                    )}
                </AnimatePresence>
            </div>
        </>
    )
}

export default AdminUniversities
